import { useQuery } from "@tanstack/react-query";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

interface CustomerStatsRow {
  date: string;
  customer: string;
  total: number;
  answered: number;
  answer_rate: number;
}

interface CustomerStatsResponse {
  status: string;
  days: number;
  rows: CustomerStatsRow[];
  prefix_rate: number;
  total_queries: number;
}

const COLORS = ["#3b82f6", "#ef4444", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899"];

/** 고객사별 일별 질문량 stacked bar chart (최근 7일) */
export function CustomerVolumeChart() {
  const { data, isLoading } = useQuery<CustomerStatsResponse>({
    queryKey: ["admin", "customer-stats", 7],
    queryFn: () => api.get("/admin/customer-stats", { days: 7 }),
    refetchInterval: 5 * 60_000,
  });

  if (isLoading || !data) {
    return <Skeleton className="h-72" />;
  }

  // rows를 날짜별·고객사별 pivot
  const customers = Array.from(new Set(data.rows.map((r) => r.customer)));
  const dateMap: Record<string, Record<string, number>> = {};
  for (const r of data.rows) {
    if (!dateMap[r.date]) dateMap[r.date] = {};
    dateMap[r.date][r.customer] = r.total;
  }
  const chartData = Object.keys(dateMap)
    .sort()
    .map((date) => ({ date, ...dateMap[date] }));

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">고객사별 질문량 (최근 7일)</h3>
          <div className="text-xs text-gray-500">
            총 {data.total_queries}건 · prefix 사용율 {data.prefix_rate}%
          </div>
        </div>

        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" fontSize={11} />
            <YAxis fontSize={11} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: "11px" }} />
            {customers.map((c, i) => (
              <Bar key={c} dataKey={c} stackId="a" fill={COLORS[i % COLORS.length]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
